import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY  || '';

function loadScript(src, id) {
  return new Promise((resolve) => {
    if (document.getElementById(id)) { resolve(); return; }
    const s = document.createElement('script');
    s.id = id; s.src = src; s.async = true; s.defer = true;
    s.onload = resolve; s.onerror = resolve;
    document.head.appendChild(s);
  });
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, ssoError } = useAuth();
  const navigate = useNavigate();

  // CAPTCHA state
  const turnstileRef    = useRef(null);
  const turnstileToken  = useRef('');
  const turnstileWidgetId = useRef(null);
  const [captchaReady, setCaptchaReady] = useState(!TURNSTILE_SITE_KEY);

  // Load + render Cloudflare Turnstile
  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || typeof window.turnstile === 'undefined') return;
    if (turnstileWidgetId.current !== null) return;
    turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: 'dark',
      callback: (token) => { turnstileToken.current = token; setCaptchaReady(true); },
      'error-callback': () => setCaptchaReady(true), // fail open
      'expired-callback': () => {
        turnstileToken.current = '';
        setCaptchaReady(false);
        window.turnstile.reset(turnstileWidgetId.current);
      },
    });
  }, []);

  useEffect(() => {
    if (TURNSTILE_SITE_KEY) {
      window.onloadTurnstileCallback = renderTurnstile;
      loadScript(
        `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback`,
        'cf-turnstile-script'
      ).then(renderTurnstile);
    }
    if (RECAPTCHA_SITE_KEY) {
      loadScript(
        `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`,
        'grecaptcha-script'
      );
    }
    return () => {
      if (turnstileWidgetId.current !== null && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [renderTurnstile]);

  // If already logged in, route by role
  useEffect(() => {
    if (user && user !== false) {
      if (user.role === 'SUB_USER') {
        navigate('/trade', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  // Show SSO error if redirected here after failed SSO
  useEffect(() => {
    if (ssoError) setError(ssoError);
  }, [ssoError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Get captcha token — Turnstile first, reCAPTCHA v3 fallback
    let captcha_token = turnstileToken.current || '';
    if (!captcha_token && RECAPTCHA_SITE_KEY && window.grecaptcha) {
      try {
        captcha_token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'login' });
      } catch { /* fail open */ }
    }

    const result = await login(email, password, captcha_token);

    if (result.success) {
      const role = result.data?.role;
      if (role === 'SUB_USER') {
        navigate('/trade', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      setError(result.error);
      // Reset Turnstile on failure so user can try again
      if (turnstileWidgetId.current !== null && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileToken.current = '';
        setCaptchaReady(!TURNSTILE_SITE_KEY);
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex login-bg">
      {/* Left side */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8520F]/80 to-[#0a0d14]/95" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-12 h-12 text-[#E8520F]" />
            <span className="text-3xl font-bold font-['Outfit']">GauriLabs Commander</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 font-['Outfit']">Unified Web3 Control Center</h1>
          <p className="text-lg text-white/80 max-w-md">
            Dual-chain wallet management, market automation, and ZK-privacy vault — all in one secure platform.
          </p>
        </div>
      </div>

      {/* Right side — Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[hsl(220,20%,8%)]">
        <Card className="w-full max-w-md border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] shadow-none">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4 lg:hidden">
              <Shield className="w-8 h-8 text-[#E8520F]" />
              <span className="text-xl font-bold font-['Outfit'] text-white">GauriLabs</span>
            </div>
            <CardTitle className="text-2xl font-['Outfit'] text-white">Welcome Back</CardTitle>
            <CardDescription className="text-[hsl(215,16%,56%)]">
              Sign in to access your command center
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[hsl(210,20%,85%)]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                  <Input id="email" type="email" placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    className="pl-10 border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white placeholder:text-[hsl(215,16%,40%)] focus:ring-2 focus:ring-[#E8520F] focus:border-transparent"
                    required data-testid="login-email-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[hsl(210,20%,85%)]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                  <Input id="password" type="password" placeholder="••••••••"
                    value={password} onChange={e => setPassword(e.target.value)}
                    className="pl-10 border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white placeholder:text-[hsl(215,16%,40%)] focus:ring-2 focus:ring-[#E8520F] focus:border-transparent"
                    required data-testid="login-password-input" />
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#E8520F] hover:bg-[#C9440C] text-white"
                disabled={loading || !captchaReady} data-testid="login-submit-btn">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : !captchaReady ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Security check...</span>
                  </div>
                ) : 'Sign In'}
              </Button>

              {/* Cloudflare Turnstile widget */}
              {TURNSTILE_SITE_KEY && (
                <div ref={turnstileRef} className="flex justify-center mt-1" />
              )}

              <p className="text-center text-sm text-[hsl(215,16%,56%)]">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#E8520F] hover:underline font-medium" data-testid="register-link">
                  Create one
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
